import React, { useRef, useEffect, Suspense, lazy } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation } from "react-router-dom";
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { ScrollSmoother } from 'gsap/ScrollSmoother';
import { useGSAP } from '@gsap/react';

// Import components that ARE needed immediately for the homepage
import Header from './components/Header';
import Hero from './components/Hero';
import Footer from './components/Footer';
import WhyChooseUs from './components/WhyChooseUs';
import MarqueeWall from './components/MarqeeWall';
import WhatYouGetSection from './components/WhatYouGet';
import ServicesShowcase from './components/CircularSteps';
import ClientTestimonials from './components/ClientTestimonials';
import TeamSection from './components/TeamSection';
import ClientLogos from './components/ClientLogos';
import FaqSection from './components/FaqComponent';


import PortfolioPage from './components/Portfolio';
import AccountDetail from './components/AccountDetails';

// ✅ FIX (restored): these routes are NOT part of the homepage, so there's
// no reason their JS should be downloaded/parsed on first load. React.lazy()
// splits each into its own chunk that's only fetched when the person
// actually navigates to that route — this directly addresses the "Reduce
// unused JavaScript" (77 KiB) finding from your live Lighthouse report.
const ContactPage = lazy(() => import('./components/ContactPage'));
const AboutUs = lazy(() => import('./components/AboutUs'));
const ServiceDetail = lazy(() => import('./components/ServiceDetails'));
const SubServiceDetail = lazy(() => import('./components/SubService'));
const InternshipComponent = lazy(() => import('./components/InternshipPage'));

import './styles/App.css';

// Register plugins
gsap.registerPlugin(ScrollTrigger, ScrollSmoother);

// Scroll to top and clean up on route change
function RouteChangeHandler() {
  const location = useLocation();

  useEffect(() => {
    // Scroll to top
    window.scrollTo(0, 0);
    
    // Kill all ScrollTriggers when leaving homepage
    return () => {
      ScrollTrigger.getAll().forEach(trigger => {
        try {
          trigger.kill(true);
        } catch (e) {
          // Ignore errors
        }
      });
    };
  }, [location.pathname]);

  return null;
}

// Homepage with ScrollSmoother
function HomePage() {
  const smootherRef = useRef(null);

  useGSAP(() => {
    // Kill any existing smoother first
    if (smootherRef.current) {
      smootherRef.current.kill();
    }

    // Initialize ScrollSmoother for homepage
    smootherRef.current = ScrollSmoother.create({
      wrapper: "#smooth-wrapper",
      content: "#smooth-content",
      smooth: 2,
      effects: true
    });

    return () => {
      if (smootherRef.current) {
        smootherRef.current.kill();
        smootherRef.current = null;
      }
      // Kill all ScrollTriggers
      ScrollTrigger.getAll().forEach(trigger => trigger.kill(true));
    };
  }, []);

  return (
    <div id="smooth-wrapper">
      <div id="smooth-content">
        <Hero />
        <ServicesShowcase />
        <WhyChooseUs />
         <MarqueeWall />
        <WhatYouGetSection />
       
        <ClientTestimonials />
        <ClientLogos />
        {/* <TeamSection /> */}
        {/* <PortalScrollSection /> */}
        <FaqSection />
        <Footer />
      </div>
    </div>
  );
}

// Service pages WITHOUT ScrollSmoother (Handles both parent and sub-service depths)
function ServicePage({ children }) {
  return <>{children}</>;
}

// Other pages WITHOUT ScrollSmoother
function OtherPages({ children }) {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  
  return <>{children}</>;
}

// ✅ Minimal, no-layout-shift fallback while a lazy route chunk loads. Kept
// intentionally plain (no spinner) so it can't itself cause a layout shift.
function RouteFallback() {
  return <div style={{ minHeight: "60vh" }} />;
}

function AppContent() {
  const location = useLocation();

  return (
    <>
      <RouteChangeHandler />
      <Header />

      <Suspense fallback={<RouteFallback />}>
        <Routes>
          {/* Level 0: Main App Landing Hub */}
          <Route path="/" element={<HomePage />} />
          <Route path="/internships" element={<InternshipComponent />} />

          {/* Level 1: Dynamic Channel Hub (TikTok Shop, Shopify, eBay) */}
          <Route path="/services/:slug" element={<ServicePage><ServiceDetail /></ServicePage>} />

          {/* Level 2: TWO-TIER NESTED SUBSERVICE ROUTE 
              Maps paths like '/services/tiktok-shop/account-creation' straight to your component matrix */}
          <Route 
                path="/services/:platform/:subservice" 
                element={<SubServiceDetail />} 
              />

          {/* Standard Content Tracks */}
          <Route path="/contact" element={<OtherPages><ContactPage /><Footer /></OtherPages>} />
          <Route path="/about-us" element={<OtherPages><AboutUs /><Footer /></OtherPages>} />
           <Route path="/portfolio" element={<OtherPages><PortfolioPage /><Footer /></OtherPages>} />
           <Route path="/portfolio/:accountName" element={<OtherPages><AccountDetail /><Footer /></OtherPages>} />
        </Routes>
      </Suspense>
    </>
  );
}

export default function App() {
  return (
    <Router>
      <AppContent />
    </Router>
  );
}